import {
  AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, inject, Input, OnChanges, Output,
  ViewChild, ViewEncapsulation,
  DOCUMENT
} from "@angular/core";

import { buildSafeFragment, findSection, linkOf, ManualLink, ManualPage } from "../manual-html";

/**
 * Renders a manual page, or one section of it, with the manual's own styling (work items 830 and
 * 882). The DOM is built from an allow-list by buildSafeFragment, never bound as HTML. Clicks on
 * links between manual pages are handed to the parent, which knows whether they open in the help
 * panel or the technical manual; external links open in a new tab.
 */
@Component({
  selector: "ngx-help-article",
  standalone: true,
  template: `<div class="manual-content" #content (click)="onClick($event)"></div>`,
  styleUrl: "./help-article.component.scss",
  // The nodes are created in code, so they never get the attribute emulated encapsulation scopes
  // styles with; the stylesheet scopes itself under the element selector instead.
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HelpArticleComponent implements OnChanges, AfterViewInit {
  @Input({ required: true }) page!: ManualPage;
  /** A section id, or an id inside one; null shows the whole page. */
  @Input() section: string | null = null;
  /** With the whole page on show, an id to bring into view, such as the address's fragment. */
  @Input() scrollTarget: string | null = null;
  /** A link to another part of the manual was clicked. */
  @Output() navigate = new EventEmitter<ManualLink>();

  @ViewChild("content", { static: true }) private content!: ElementRef<HTMLElement>;
  private readonly document = inject(DOCUMENT);

  ngOnChanges(): void {
    this.render();
  }

  ngAfterViewInit(): void {
    this.render();
  }

  /** Brings an element with this id into view, if it is on show. */
  scrollTo(id: string): boolean {
    const element = this.content?.nativeElement.querySelector(`[id="${CSS.escape(id)}"]`);
    element?.scrollIntoView({ block: "start" });
    return !!element;
  }

  onClick(event: MouseEvent): void {
    // Modified clicks keep their browser meaning, such as opening the page in a new tab.
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const anchor = (event.target as Element | null)?.closest?.("a[data-manual-link]");
    const link = anchor ? linkOf(anchor) : null;
    if (!link) return;
    event.preventDefault();
    this.navigate.emit(link);
  }

  private render(): void {
    const host = this.content?.nativeElement;
    if (!host || !this.page) return;

    const section = findSection(this.page, this.section);
    const nodes = section
      ? section.nodes
      : this.page.sections.reduce<readonly Node[]>((all, s) => [...all, ...s.nodes], this.page.intro);
    host.replaceChildren(buildSafeFragment(nodes, this.document));

    // A link to an h3 opens its section and then has to land on the h3 itself.
    if (section && this.section && this.section !== section.id) this.scrollTo(this.section);
    else if (!section && this.scrollTarget) this.scrollTo(this.scrollTarget);
  }
}
